import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './GrievanceForm.css';
import Select from 'react-select';
import { getData, postDataWithBody } from '../../../../Services/Services';
import {getThemeURL, GrievanceCreate } from '../../../../Services/EndPoints';

const GrievanceForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    alternativePhone: '',
    relationship: '',
    propertyAddress: '',
    unitNumber: '',
    category: '',
    priority: '',
    subject: '',
    description: '',
    attachments: []
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [username, setusername] = useState([]);
  const navigate = useNavigate();

  // Validation patterns
  const validationPatterns = {
    fullName: /^[a-zA-Z\s.'-]{2,50}$/, // Only letters, spaces, and common name characters
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, // Standard email pattern
    phone: /^[0-9]{10}$/, // Exactly 10 digits
    alternativePhone: /^$|^[0-9]{10}$/, // Optional but must be 10 digits if provided
    relationship: /.+/, // Non-empty
    propertyAddress: /^[\s\S]{10,200}$/, // 10-200 characters
    unitNumber: /^$|^[\s\S]{1,50}$/, // Optional, alphanumeric with spaces and hyphens
    category: /.+/, // Non-empty
    priority: /.+/, // Non-empty
    subject: /^.{5,100}$/, // 5-100 characters
    description: /^[\s\S]{20,1000}$/ // 20-1000 characters
  };

  const validationMessages = {
    fullName: 'Full Name must contain only letters, spaces, and common name characters (2-50 characters)',
    email: 'Please enter a valid Email ID',
    phone: 'Phone number must be exactly 10 digits',
    alternativePhone: 'Alternative number must be exactly 10 digits (optional)',
    relationship: 'Relationship is required',
    propertyAddress: 'Property address must be between 10 and 200 characters',
    unitNumber: 'Unit number must be between 1 and 50 characters',
    category: 'Category is required',
    priority: 'Priority Level is required',
    subject: 'Title must be between 5 and 100 characters',
    description: 'Description must be between 20 and 1000 characters'
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Apply input restrictions based on field type
    let filteredValue = value;
    
    switch(name) {
      case 'fullName':
        // Allow only letters, spaces, and common name characters
        filteredValue = value.replace(/[^a-zA-Z\s.'-]/g, '');
        break;
      case 'phone':
      case 'alternativePhone':
        // Allow only numbers
        filteredValue = value.replace(/\D/g, '').slice(0, 10);
        break;
      case 'subject':
        // Allow alphanumeric and common punctuation
        filteredValue = value.slice(0, 100);
        break;
      case 'description':
        // Allow alphanumeric and common punctuation
        filteredValue = value.slice(0, 1000);
        break;
      case 'propertyAddress':
        // Allow alphanumeric, spaces, and common address characters
        filteredValue = value.slice(0, 200);
        break;
      case 'unitNumber':
        // Allow alphanumeric, spaces, and hyphens
        filteredValue = value.slice(0, 50);
        break;
      default:
        filteredValue = value;
    }
    
    setFormData(prev => ({ ...prev, [name]: filteredValue }));
    
    // Real-time validation for specific fields
    if (name === 'email') {
      validateEmail(value);
    } else if (name === 'phone' || name === 'alternativePhone') {
      validatePhoneNumber(name, value);
    } else if (validationPatterns[name]) {
      validateField(name, filteredValue);
    }
  };

  const validateField = (fieldName, value) => {
    const pattern = validationPatterns[fieldName];
    const message = validationMessages[fieldName];
    
    if (!pattern.test(value)) {
      setErrors(prev => ({ ...prev, [fieldName]: message }));
      return false;
    } else {
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
      return true;
    }
  };

  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid Email ID' }));
      return false;
    }
    setErrors(prev => ({ ...prev, email: '' }));
    return true;
  };

  const validatePhoneNumber = (fieldName, phone) => {
    const phonePattern = /^[0-9]{10}$/;
    if (phone && !phonePattern.test(phone)) {
      setErrors(prev => ({ ...prev, [fieldName]: 'Phone number must be exactly 10 digits' }));
      return false;
    }
    setErrors(prev => ({ ...prev, [fieldName]: '' }));
    return true;
  };

  const handleSelectChange = (selectedOption, name) => {
    setFormData(prev => ({ ...prev, [name]: selectedOption.value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      if (!validTypes.includes(file.type)) {
        toast.error(`File ${file.name} has invalid type. Only PNG, JPG, PDF are allowed.`, {
          position: "top-center",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        return false;
      }
      
      if (file.size > maxSize) {
        toast.error(`File ${file.name} is too large. Maximum size is 10MB.`, {
          position: "top-center",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        return false;
      }
      
      return true;
    });
    
    setFormData(prev => ({ ...prev, attachments: [...prev.attachments, ...validFiles] }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate each field
    Object.keys(validationPatterns).forEach(fieldName => {
      const value = formData[fieldName];
      const pattern = validationPatterns[fieldName];
      
      // Skip validation for optional fields if empty
      if ((fieldName === 'alternativePhone' || fieldName === 'unitNumber') && value === '') {
        return;
      }
      
      if (!pattern.test(value)) {
        newErrors[fieldName] = validationMessages[fieldName];
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  useEffect(()=>{
    getUserdata()
  },[])

  
  const getUserdata = async () => {
    try {
      const usr_prof = await getData(getThemeURL);
      console.log("User Data:", usr_prof);
      setusername(usr_prof[0]);

      // Pre-populate form fields with user data
      if (usr_prof[0]) {
        const userData = usr_prof[0];

        // Concatenate first_name and last_name
        const fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();

        setFormData(prev => ({
          ...prev,
          fullName: fullName,
          email: userData.user_name || '',
          phone: userData.phone_number || ''
        }));
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast.error('Failed to load user data. Please refresh the page.', {
        position: "top-center",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };


  const SubmitForm = async (request, attachments) => {
  try {
    const formData = new FormData();

    // Add grievance JSON
    formData.append('grievance', JSON.stringify(request));

    if (attachments && attachments.length > 0) {
      // Append selected files
      attachments.forEach((file) => {
        formData.append('files', file);
      });
    } else {
      // IMPORTANT: send files as binary null
      formData.append('files', new Blob([], { type: 'application/octet-stream' }));
    }

    const response = await postDataWithBody(GrievanceCreate, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response;
  } catch (error) {
    console.error('Error creating ticket:', error);
    throw error;
  }
};


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill all the mandatory fields before submitting.', {
        position: "top-center",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }
    
    setIsSubmitting(true);
    
    // Show loading toast
    const toastId = toast.loading('Submitting your grievance...', {
      position: "top-center",
      autoClose: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
    });
    
    try {
      // Prepare the request object matching your API structure
      const request = {
        customerName: formData.fullName,
        phoneNumber: formData.phone,
        email: formData.email,
        relPropertyName: formData.relationship,
        propertyAddress: formData.propertyAddress,
        aprtmentNumber: formData.unitNumber,
        categoryName: formData.category,
        priority: formData.priority,
        description: formData.description,
        subject: formData.subject,
        createdBy: username?.id || 4,
        roleName: 'U',
        alternatePhoneNumber: formData.alternativePhone
      };
      
      // Call the API with both request and attachments
      const response = await SubmitForm(request, formData.attachments);
      
      console.log('Form submitted successfully:', response);
      
      // Update loading toast to success
      toast.update(toastId, {
        render: 'Grievance submitted successfully! Redirecting to tickets...',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Reset form after successful submission
      setFormData({
        fullName: username ? `${username.first_name || ''} ${username.last_name || ''}`.trim() : '',
        email: username?.user_name || '',
        phone: username?.phone_number || '',
        alternativePhone: '',
        relationship: '',
        propertyAddress: '',
        unitNumber: '',
        category: '',
        priority: '',
        subject: '',
        description: '',
        attachments: []
      });
      
      // Navigate to tickets page after 2 seconds
      setTimeout(() => {
        navigate('/grievance-rental-tickets');
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Update loading toast to error
      toast.update(toastId, {
        render: 'Failed to submit grievance. Please try again.',
        type: 'error',
        isLoading: false,
        autoClose: 4000,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '40px',
      height: '40px',
      borderRadius: '4px',
      borderColor: state.isFocused ? '#4a6cf7' : errors[state.selectProps.name] ? '#e53e3e' : '#cccccc',
      boxShadow: state.isFocused ? '0 0 0 1px #4a6cf7' : 'none',
      '&:hover': {
        borderColor: errors[state.selectProps.name] ? '#e53e3e' : '#4a6cf7'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      height: '40px',
      padding: '0 8px'
    }),
    input: (base) => ({
      ...base,
      margin: '0',
      padding: '0'
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: '40px'
    })
  };

  const relationshipOptions = [
    { value: 'Tenant', label: 'Tenant' },
    { value: 'Owner', label: 'Owner' },
    { value: 'Applicant', label: 'Applicant' }
  ];

  const categoryOptions = [
    { value: 'Maintenance & Repairs', label: 'Maintenance & Repairs' },
    { value: 'Rent / Payment', label: 'Rent / Payment' },
    { value: 'Amenities Issues', label: 'Amenities Issues' },
    { value: 'Noise Complaints', label: 'Noise Complaints' },
    { value: 'Safety / Security issues', label: 'Safety / Security issues' },
    { value: 'Other', label: 'Other' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' }
  ];

  // File upload icon component
  const FileUploadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
    </svg>
  );

  return (
    <>
      <ToastContainer 
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{
          marginTop: '70px', // Adjust based on your navbar height
          zIndex: 9999
        }}
      />
      
      <form className="grievance-form" onSubmit={handleSubmit} style={{background:"#EFEFEF"}}>
        <h2><i className="fas fa-user"></i> User Details</h2>
        <div className="form-section">
          <div className="form-group half-width">
            <label className="required">
              <svg className="label-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3Zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
              </svg>
              Full Name</label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Enter your full name (letters only)"
              className={errors.fullName ? 'error' : 'input-field'}
              maxLength="50"
              disabled={isSubmitting}
            />
            {errors.fullName && <span className="error-message">{errors.fullName}</span>}
          </div>

          <div className="form-group half-width">
            <label className="required">
              <svg className="label-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M.05 3.555A2 2 0 0 1 2 2h12a2 2 0 0 1 1.95 1.555L8 8.414.05 3.555ZM0 4.697v7.104l5.803-3.558L0 4.697ZM6.761 8.83l-6.57 4.027A2 2 0 0 0 2 14h12a2 2 0 0 0 1.808-1.144l-6.57-4.027L8 9.586l-1.239-.757Zm3.436-.586L16 11.801V4.697l-5.803 3.546Z" />
              </svg>
              Email ID</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your Email ID"
              className={errors.email ? 'error' : 'input-field'}
              disabled={isSubmitting}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group half-width">
            <label className="required">
              <svg className="label-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/>
              </svg>
              Phone Number</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter 10-digit phone number"
              className={errors.phone ? 'error' : 'input-field'}
              maxLength="10"
              disabled={isSubmitting}
            />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
            <div className="hint">Digits only (10 characters)</div>
          </div>

          <div className="form-group half-width">
            <label>
              <svg className="label-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z" />
              </svg>
              Alternative Number
            </label>
            <input
              type="tel"
              name="alternativePhone"
              value={formData.alternativePhone}
              onChange={handleChange}
              placeholder="Enter 10-digit alternative number (optional)"
              className={errors.alternativePhone ? 'error' : 'input-field'}
              maxLength="10"
              disabled={isSubmitting}
            />
            {errors.alternativePhone && <span className="error-message">{errors.alternativePhone}</span>}
            <div className="hint">Digits only (optional, 10 characters)</div>
          </div>

          <div className="form-group half-width">
            <label className="required">
              <svg className="label-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
              </svg>
              Relationship to Property</label>
            <Select
              options={relationshipOptions}
              onChange={(option) => handleSelectChange(option, 'relationship')}
              value={relationshipOptions.find(opt => opt.value === formData.relationship)}
              classNamePrefix="react-select"
              className={errors.relationship ? 'error' : 'input-field'}
              placeholder="Select relationship"
              isSearchable={false}
              styles={customSelectStyles}
              name="relationship"
              isDisabled={isSubmitting}
            />
            {errors.relationship && <span className="error-message">{errors.relationship}</span>}
          </div>
        </div>

        <h2><i className="fas fa-home"></i> Property Details</h2>
        <div className="form-section">
          <div className="form-group half-width">
            <label className="required">
              <svg className="label-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M12.166 8.94c-.524 1.062-1.234 2.12-1.96 3.07A31.493 31.493 0 0 1 8 14.58a31.481 31.481 0 0 1-2.206-2.57c-.726-.95-1.436-2.008-1.96-3.07C3.304 7.867 3 6.862 3 6a5 5 0 0 1 10 0c0 .862-.305 1.867-.834 2.94zM8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10z"/>
                <path d="M8 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/>
              </svg>
              Property Address</label>
            <textarea
              name="propertyAddress"
              value={formData.propertyAddress}
              onChange={handleChange}
              placeholder="Enter complete property address (10-200 characters)"
              rows="3"
              className={errors.propertyAddress ? 'error' : 'textarea-field'}
              maxLength="200"
              disabled={isSubmitting}
            />
            {errors.propertyAddress && <span className="error-message">{errors.propertyAddress}</span>}
            <div className="hint">{formData.propertyAddress.length}/200 characters</div>
          </div>
          <div className="form-group half-width">
            <label>
              <svg className="label-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M4.5 9a3.5 3.5 0 1 0 0 7h7a3.5 3.5 0 1 0 0-7h-7zm7 6a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm-7-14a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zm2.45 0A3.49 3.49 0 0 1 8 3.5 3.49 3.49 0 0 1 6.95 6h4.55a2.5 2.5 0 0 0 0-5H6.95zM4.5 0h7a3.5 3.5 0 1 1 0 7h-7a3.5 3.5 0 1 1 0-7z"/>
              </svg>
              Unit/Apartment Number</label>
            <textarea
              type="text"
              name="unitNumber"
              value={formData.unitNumber}
              onChange={handleChange}
              placeholder="Unit, Apartment, or Suite number"
              rows="3"
              className={errors.unitNumber ? 'error' : 'textarea-field'}
              maxLength="50"
              disabled={isSubmitting}
            />
            {errors.unitNumber && <span className="error-message">{errors.unitNumber}</span>}
            <div className="hint">Alphanumeric only (optional, max 50 characters)</div>
          </div>
        </div>

        <h2><i className="fas fa-exclamation-triangle"></i> Issue Details</h2>
        <div className="form-section">
          <div className="form-group half-width">
            <label className="required">
              <svg className="label-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z"/>
              </svg>
              Category</label>
            <Select
              options={categoryOptions}
              onChange={(option) => handleSelectChange(option, 'category')}
              value={categoryOptions.find(opt => opt.value === formData.category)}
              classNamePrefix="react-select"
              className={errors.category ? 'error' : 'input-field'}
              placeholder="Select category"
              isSearchable={false}
              styles={customSelectStyles}
              name="category"
              isDisabled={isSubmitting}
            />
            {errors.category && <span className="error-message">{errors.category}</span>}
          </div>

          <div className="form-group half-width">
            <label className="required">
              <svg className="label-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M3.5 2A1.5 1.5 0 0 1 5 3.5V5h3.5a1.5 1.5 0 0 1 1.5 1.5v2a1.5 1.5 0 0 1-1.5 1.5H5v.5a1.5 1.5 0 0 1-1.5 1.5h-2A1.5 1.5 0 0 1 0 10.5v-2A1.5 1.5 0 0 1 1.5 7h2A1.5 1.5 0 0 1 5 8.5V8h3.5a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5H5v-.5A1.5 1.5 0 0 1 3.5 4h-2A1.5 1.5 0 0 1 0 2.5v-2A1.5 1.5 0 0 1 1.5 0h2A1.5 1.5 0 0 1 5 1.5V2h3.5a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5h-9a.5.5 0 0 0-.5.5v2a.5.5 0 0 0 .5.5H4v-.5A.5.5 0 0 0 3.5 1h-2a.5.5 0 0 0-.5.5v2a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .5-.5V3H9v1.5a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5h-2a.5.5 0 0 0-.5.5V3H5V1.5a.5.5 0 0 0-.5-.5h-2a.5.5 0 0 0-.5.5v2a.5.5 0 0 0 .5.5h2z"/>
              </svg>
              Priority Level</label>
            <Select
              options={priorityOptions}
              onChange={(option) => handleSelectChange(option, 'priority')}
              value={priorityOptions.find(opt => opt.value === formData.priority)}
              classNamePrefix="react-select"
              className={errors.priority ? 'error' : 'input-field'}
              placeholder="Select priority"
              isSearchable={false}
              styles={customSelectStyles}
              name="priority"
              isDisabled={isSubmitting}
            />
            {errors.priority && <span className="error-message">{errors.priority}</span>}
          </div>

          <div className="form-group full-width">
            <label className="required">
              <svg className="label-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z"/>
              </svg>
              Title</label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="Brief summary of your grievance (5-100 characters)"
              className={errors.subject ? 'error' : 'input-field'}
              maxLength="100"
              disabled={isSubmitting}
            />
            {errors.subject && <span className="error-message">{errors.subject}</span>}
            <div className="hint">{formData.subject.length}/100 characters</div>
          </div>

          <div className="form-group full-width">
            <label className="required">
              <svg className="label-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M5 10.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5z"/>
                <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2zm0 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3z"/>
              </svg>
              Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Please provide detailed description of the issue (20-1000 characters)"
              rows="5"
              className={errors.description ? 'error' : 'textarea-field'}
              maxLength="1000"
              disabled={isSubmitting}
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
            <div className="hint">{formData.description.length}/1000 characters</div>
          </div>

          <div className="form-group full-width">
            <label>Attachments</label>
            <div className="file-input-wrapper">
              <label className="file-input-label">
                <FileUploadIcon />
                <span>Click to upload files</span><br/>
                <span style={{ fontSize: '0.875rem', color: '#718096' }}>
                  PNG / JPG / PDF (Max 10 MB each)
                </span>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  accept=".png,.jpg,.jpeg,.pdf"
                  disabled={isSubmitting}
                />
              </label>
            </div>
            {formData.attachments.length > 0 && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#4a5568' }}>
                {formData.attachments.length} file(s) selected
              </div>
            )}

            {formData.attachments.length > 0 && (
              <div className="file-preview-wrapper" style={{ marginTop: '1rem' }}>
                <h4>Selected Files:</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {formData.attachments.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        position: 'relative',
                        width: '100px',
                        height: '100px',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        background: '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {file.type.startsWith('image/') ? (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`attachment-${index}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            padding: '4px',
                            textAlign: 'center',
                            fontSize: '11px',
                            lineHeight: '1.2',
                            wordBreak: 'break-word'
                          }}
                        >
                          {/* show only file icon + truncated name */}
                          <div style={{ fontSize: '20px', marginBottom: '4px' }}>📄</div>
                          <span
                            title={file.name}
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {file.name}
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const newAttachments = formData.attachments.filter((_, i) => i !== index);
                          setFormData(prev => ({ ...prev, attachments: newAttachments }));
                        }}
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          background: 'red',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        disabled={isSubmitting}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <button type="submit" className="submit-btn" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <span className="spinner"></span>
              Submitting...
            </>
          ) : (
            'Submit Grievance'
          )}
        </button>
      </form>
    </>
  );
};

export default GrievanceForm;